import getLocaleMessages from '../utils/localesMessages';
import _isEmpty from 'lodash/isEmpty';
import _merge from 'lodash/merge';
import {
  isValidSecureUrl,
  isValidUrl,
  validateExtraVariables,
  byteLength,
} from '../utils/clientValidatorUtils';
import {
  normalizeNumber,
  formatCurrencyNoDecimals,
} from '../utils/amountUtils';
import { LOGO_TARGET_BUTTON } from '../reducers/reducerButtonFactory';

const {
  amount = {},
  url = {},
  required,
  programs: { atleastOne, duplicatedProgram },
  genericSizeError,
} = getLocaleMessages('buttonsV3/fieldErrors');

const MAX_TEXT_LENGTH = 127;
const MAX_NOTE_LENGTH = 40;
export const MAX_BUTTON_NAME_LENGTH = 50;
const invalidProgramSizeError = genericSizeError.replace(
  '{maxSize}',
  MAX_TEXT_LENGTH
);
const invalidPurposeSizeError = genericSizeError.replace(
  '{maxSize}',
  MAX_TEXT_LENGTH
);
const invalidNameSizeError = genericSizeError.replace(
  '{maxSize}',
  MAX_BUTTON_NAME_LENGTH
);
const invalidNoteSizeError = genericSizeError.replace(
  '{maxSize}',
  MAX_NOTE_LENGTH
);

export function validate(values) {
  const errors = {};

  // Validation for buttonImageUrl
  if (values.usePaypalButtonStyle === 'no') {
    const buttonImageUrl = values.buttonImageUrl;

    if (!buttonImageUrl) {
      errors.buttonImageUrl = required;
    } else if (!isValidUrl(buttonImageUrl)) {
      errors.buttonImageUrl = url.invalid;
    }
  }

  if (!values.receiverCurrency) {
    errors.receiverCurrency = required;
  }

  const currencyParams = {
    currency: values.receiverCurrency,
    value: 1,
  };
  const minimumDonationError =
    values.receiverCurrency &&
    amount.minimumDonationUS.replace(
      '{amount}',
      formatCurrencyNoDecimals(currencyParams)
    );

  if (values.amountType === 'fixed') {
    const isUSD = ['USD'].indexOf(values.receiverCurrency) >= 0;
    if (_isEmpty(values.amountValue)) {
      errors.amountValue = amount.required;
    } else if (isUSD && parseFloat(normalizeNumber(values.amountValue)) < 1) {
      errors.amountValue = minimumDonationError;
    } else if (!isUSD && normalizeNumber(parseFloat(values.amountValue)) <= 0) {
      errors.amountValue = amount.required;
    }
  }

  if (values.amountType === 'merchant_recommended') {
    const isUSD = ['USD'].indexOf(values.receiverCurrency) >= 0;
    if (_isEmpty(values.merchantAmount1)) {
      errors.merchantAmount1 = amount.required;
    } else if (
      isUSD &&
      normalizeNumber(parseFloat(values.merchantAmount1)) < 1
    ) {
      errors.merchantAmount1 = minimumDonationError;
    } else if (
      !isUSD &&
      normalizeNumber(parseFloat(values.merchantAmount1)) <= 0
    ) {
      errors.merchantAmount1 = amount.required;
    }
    if (_isEmpty(values.merchantAmount2)) {
      errors.merchantAmount2 = amount.required;
    } else if (
      isUSD &&
      normalizeNumber(parseFloat(values.merchantAmount2)) < 1
    ) {
      errors.merchantAmount2 = minimumDonationError;
    } else if (
      !isUSD &&
      normalizeNumber(parseFloat(values.merchantAmount2)) <= 0
    ) {
      errors.merchantAmount2 = amount.required;
    }

    if (_isEmpty(values.merchantAmount3)) {
      errors.merchantAmount3 = amount.required;
    } else if (
      isUSD &&
      normalizeNumber(parseFloat(values.merchantAmount3)) < 1
    ) {
      errors.merchantAmount3 = minimumDonationError;
    } else if (
      !isUSD &&
      normalizeNumber(parseFloat(values.merchantAmount3)) <= 0
    ) {
      errors.merchantAmount3 = amount.required;
    }
  }

  if (
    values.logoTarget === LOGO_TARGET_BUTTON &&
    !isValidSecureUrl(values.logoUrl)
  ) {
    errors.logoUrl = url.insecure;
  }

  if (values.cancelReturn && !isValidUrl(values.cancelReturn)) {
    errors.cancelReturn = url.generic;
  }

  if (values.return && !isValidUrl(values.return)) {
    errors.return = url.generic;
  }

  const extraVariablesError = validateExtraVariables(values.extraVariablesView);
  if (extraVariablesError) {
    errors.extraVariablesView = extraVariablesError;
  }

  // Check text field length
  if (values.purpose && byteLength(values.purpose) > MAX_TEXT_LENGTH) {
    errors.purpose = invalidPurposeSizeError;
  }

  if (values.noteLabel && values.noteLabel.length > MAX_NOTE_LENGTH) {
    errors.noteLabel = invalidNoteSizeError;
  }

  if (values.buttonName && values.buttonName.length > MAX_BUTTON_NAME_LENGTH) {
    errors.buttonName = invalidNameSizeError;
  }

  if (values.itemNumber && byteLength(values.itemNumber) > MAX_TEXT_LENGTH) {
    errors.itemNumber = invalidProgramSizeError;
  }

  const programErrors = validatePrograms(values);
  if (!_isEmpty(programErrors)) {
    _merge(errors, programErrors);
  }

  return errors;
}

export function validatePrograms(values) {
  const errors = {};
  if (values.listPrograms === 'no') {
    return;
  }

  const programNames = values.programNames ?? [];

  if (programNames.length > 1) {
    for (let i = 0; i < programNames.length; i++) {
      if (byteLength(programNames[i]) > MAX_TEXT_LENGTH) {
        errors[`programInput${i}`] = invalidProgramSizeError;
      } else if (validateDuplication(programNames[i], i, programNames)) {
        errors[`programInput${i}`] = duplicatedProgram;
      } else if (_isEmpty(programNames[i])) {
        errors[`programInput${i}`] = atleastOne;
      }
    }
  } else {
    if (_isEmpty(programNames[0])) {
      errors.programInput0 = atleastOne;
    }
    if (
      !_isEmpty(programNames[0]) &&
      byteLength(programNames[0]) > MAX_TEXT_LENGTH
    ) {
      errors.programInput0 = invalidProgramSizeError;
    }
  }
  return errors;
}

function validateDuplication(value, position, programNames) {
  let duplicated = false;
  for (let i = 0; i < programNames.length; i++) {
    if (value === programNames[i] && position !== i) {
      duplicated = true;
      break;
    }
  }
  return duplicated;
}
